import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { toast } from 'sonner';
import { useWorkspace } from './WorkspaceContext';
import { differenceInHours, differenceInMinutes, addHours } from 'date-fns';
import GnomeToast from '../components/gnomes/GnomeToast';

const GnomeContext = createContext();

export const useGnomeContext = () => {
    const context = useContext(GnomeContext);
    if (!context) {
        throw new Error('useGnomeContext must be used within a GnomeProvider');
    }
    return context;
};

export const GnomeProvider = ({ children }) => {

    const { activeWorkspace } = useWorkspace();
    const [gnomes, setGnomes] = useState([]);
    const [insights, setInsights] = useState([]);
    const [gnomeNotifications, setGnomeNotifications] = useState([]);
    const [latestInsightDate, setLatestInsightDate] = useState(null); // Date object
    const [generating, setGenerating] = useState(false);
    const [now, setNow] = useState(new Date());

    // Use ref to access latest gnomes in subscription without re-subscribing
    const gnomesRef = useRef(gnomes);
    gnomesRef.current = gnomes;

    // Use ref to access latest notifications in callback without re-subscribing
    const gnomeNotificationsRef = useRef(gnomeNotifications);
    gnomeNotificationsRef.current = gnomeNotifications;

    // Auto-refresh 'now' every 10 seconds to update countdowns more responsively
    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 10000);
        return () => clearInterval(interval);
    }, []);

    // Chat Modal State
    const [chatModalOpen, setChatModalOpen] = useState(false);
    const [selectedGnome, setSelectedGnome] = useState(null);
    const [selectedInsight, setSelectedInsight] = useState(null);
    const [chatDrafts, setChatDrafts] = useState({}); // Map of gnome.id to draft text

    // 1. Fetch Gnomes (Metadata)
    useEffect(() => {
        const fetchGnomes = async () => {
            const { data } = await supabase.from('gnomes').select('*').order('id');
            if (data) {
                // Apply specific mapping/ordering if needed (same as GnomesDropdown logic if we want consistency here)
                // For now, raw data is fine, components can map it.
                // But GnomesDropdown has specific color logic. Maybe we should centralize that mapping here?
                // For simplicity, let's store raw data and let GnomesDropdown do its mapping,
                // OR we can just store the enhanced objects here.
                // GnomesDropdown logic is complex (mapping names to colors). I'll replicate it here to have consistent "gnome objects" globally.

                const gnomeMap = {
                    'Strategy': { name: 'Gnomepoleon', color: 'bg-yellow-500', headshot: '/Gnomepoleon.webp' },
                    'Engagement': { name: 'Gnorm', color: 'bg-green-600', headshot: '/Gnorm.webp' },
                    'Nurture & Automation': { name: 'Stampson', color: 'bg-red-600', headshot: '/Stampson.webp' },
                    'Email Automation': { name: 'Stampson', color: 'bg-red-600', headshot: '/Stampson.webp' },
                    'Campaigns': { name: 'Benjamin', color: 'bg-blue-600', headshot: '/Benjamin.webp' },
                    'Branding': { name: 'Kestrel', color: 'bg-purple-600', headshot: '/Kestrel.webp' },
                    'Analytics': { name: 'Doug', color: 'bg-charcoal-900', headshot: '/Doug.webp' },
                    'Lead Prospector': { name: 'Doug', color: 'bg-charcoal-900', headshot: '/Doug.webp' },
                    'Chief of Staff': { name: 'Jeeves', color: 'bg-orange-600', headshot: '/Jeeves.webp' },
                    'Butler': { name: 'Jeeves', color: 'bg-orange-600', headshot: '/Jeeves.webp' }
                };

                const enhancedGnomes = data.map(g => {
                    // Normalize title for matching
                    const title = g.title || '';
                    let key = title;

                    if (title.includes('Campaigns')) key = 'Campaigns';
                    else if (title.includes('Lead Prospector')) key = 'Lead Prospector';
                    else if (title.includes('Analytics')) key = 'Analytics';
                    else {
                        key = Object.keys(gnomeMap).find(k => title.includes(k)) || title;
                    }

                    const map = gnomeMap[key] || { name: title, color: g.color || 'bg-charcoal-500' };
                    return {
                        ...g,
                        displayName: map.name,
                        displayColor: map.color,
                        headshot: map.headshot
                    };
                }).sort((a, b) => {
                     const order = ['Stampson', 'Jeeves', 'Gnomepoleon', 'Gnorm', 'Benjamin', 'Kestrel', 'Doug'];
                     return order.indexOf(a.displayName) - order.indexOf(b.displayName);
                });

                setGnomes(enhancedGnomes);
            }
        };
        fetchGnomes();
    }, []);

    // 2. Fetch Insights Logic
    const fetchInsightsData = async () => {
        if (!activeWorkspace) {
            setInsights([]);
            setGnomeNotifications([]);
            setLatestInsightDate(null);
            return;
        }

        // Unread insights
        const { data: unread } = await supabase
            .from('gnome_insights')
            .select('*')
            .eq('workspace_id', activeWorkspace.id)
            .eq('is_read', false)
            .order('created_at', { ascending: false });

        if (unread) setInsights(unread);

        // Unread gnome notifications
        const { data: unreadNotifs } = await supabase
            .from('notifications')
            .select('*')
            .eq('workspace_id', activeWorkspace.id)
            .eq('is_read', false)
            .ilike('link', '/gnomes/%')
            .order('created_at', { ascending: false });

        if (unreadNotifs) {
            const mappedNotifs = unreadNotifs.map(n => {
                const pathParts = (n.link || '').split('/');
                const gnomeId = pathParts[pathParts.length - 1];
                return { ...n, gnome_id: gnomeId, source: 'notification' };
            });
            setGnomeNotifications(mappedNotifs);
        } else {
            setGnomeNotifications([]);
        }

        // Latest insight (any status) for cooldown
        const { data: latest } = await supabase
            .from('gnome_insights')
            .select('created_at')
            .eq('workspace_id', activeWorkspace.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

        if (latest) {
            setLatestInsightDate(new Date(latest.created_at));
        } else {
            setLatestInsightDate(null);
        }
    };

    // Force fetch on mount to ensure button state is correct
    useEffect(() => {
        fetchInsightsData();
    }, [activeWorkspace]);

    // Subscription & Initial Fetch (Merged with Toast Logic)
    useEffect(() => {
        if (!activeWorkspace) return;

        fetchInsightsData();

        const channel = supabase
            .channel(`gnome_context_subscription:${activeWorkspace.id}`)
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'gnome_insights',
                filter: `workspace_id=eq.${activeWorkspace.id}`
            }, () => {
                fetchInsightsData();
            })
            .on('postgres_changes', {
                event: '*',
                schema: 'public',
                table: 'notifications',
                filter: `workspace_id=eq.${activeWorkspace.id}`
            }, (payload) => {
                // Always refresh data to ensure consistency
                // fetchInsightsData(); // Don't do full fetch on every notif, just insert

                // Handle Toast specifically for INSERT
                if (payload.eventType === 'INSERT') {
                    const newNotification = payload.new;

                    // Only process gnome-related notifications
                    if (newNotification.link && newNotification.link.startsWith('/gnomes/')) {
                        // Extract gnome ID
                        const pathParts = (newNotification.link || '').split('/');
                        const gnomeIdStr = pathParts[pathParts.length - 1];

                        // Add to state if unread
                        if (!newNotification.is_read) {
                            setGnomeNotifications(prev => {
                                 if (prev.some(n => n.id === newNotification.id)) return prev;
                                 return [{ ...newNotification, gnome_id: gnomeIdStr, source: 'notification' }, ...prev];
                            });
                        }

                        // Use Ref to get current gnomes without triggering effect re-run
                        const currentGnomes = gnomesRef.current;
                        const gnome = currentGnomes.find(g => String(g.id) === String(gnomeIdStr));

                        if (gnome) {
                            // Determine displayGnome for toast visuals.
                            // The edge function sets the notification title to "Message from [Persona Name]".
                            let displayGnome = gnome;
                            if (gnomeIdStr === '0') {
                                const titleMatch = newNotification.title?.match(/Message from (.+)/i);
                                if (titleMatch && titleMatch[1]) {
                                    const personaName = titleMatch[1].trim().toLowerCase();
                                    const foundGnome = currentGnomes.find(g => {
                                        // Simple heuristic without relying on dynamic imports/getGnomeName since we can't easily await it synchronously here
                                        const nameMaps = {
                                            'kestrel': 'Branding',
                                            'gnomepoleon': 'Strategy',
                                            'doug': 'Analytics',
                                            'benjamin': 'Campaigns',
                                            'stampson': 'Email Automation',
                                            'gnorm': 'Engagement',
                                            'jeeves': 'Chief of Staff'
                                        };
                                        const mappedTitle = nameMaps[personaName];
                                        return g.title.toLowerCase() === personaName ||
                                               (mappedTitle && g.title.toLowerCase() === mappedTitle.toLowerCase());
                                    });
                                    if (foundGnome) {
                                        displayGnome = foundGnome;
                                    }
                                }
                            }

                            toast.custom((t) => (
                                <GnomeToast
                                    gnome={displayGnome}
                                    title={newNotification.title}
                                    message={newNotification.message}
                                    onClick={() => {
                                        toast.dismiss(t);
                                        // Always open the actual chat room (Team Meeting if gnomeIdStr === '0')
                                        openChat(gnome);
                                        // markGnomeNotificationsAsRead is called inside openChat
                                    }}
                                    onDismiss={() => toast.dismiss(t)}
                                />
                            ), { duration: 5000 });
                        }
                    }
                }
            })
            .subscribe((status) => {
                if (status === 'SUBSCRIBED') {
                    console.log('GnomeToast: Connected to realtime notifications.');
                }
                if (status === 'CHANNEL_ERROR') {
                    console.error('GnomeToast: Realtime subscription error.');
                }
            });

        return () => {
            supabase.removeChannel(channel);
        };
    }, [activeWorkspace]);

    // 3. Generate Insights
    const generateInsights = async () => {};

    // Mark notifications as read
    // If gnomeId is null, mark ALL gnome notifications as read
    const markGnomeNotificationsAsRead = React.useCallback(async (gnomeId = null, shouldFetch = true) => {
        if (!activeWorkspace) return;

        // Ensure gnomeId is compared as string
        const targetGnomeId = gnomeId !== null ? String(gnomeId) : null;

        // Identify IDs to update from current state (using Ref to avoid dependency loop)
        const currentNotifs = gnomeNotificationsRef.current;
        const notifsToMark = currentNotifs.filter(n => {
            if (targetGnomeId === null) return true; // Mark all
            return String(n.gnome_id) === targetGnomeId;
        });

        const idsToUpdate = notifsToMark.map(n => n.id);

        if (idsToUpdate.length === 0) return;

        // Optimistic update: Remove notifications matching the gnomeId (or all if null)
        setGnomeNotifications(prev => prev.filter(n => {
             if (targetGnomeId === null) return false; // Clear all
             return String(n.gnome_id) !== targetGnomeId;
        }));

        try {
            // Precise ID-based update
            const { error } = await supabase
                .from('notifications')
                .update({ is_read: true })
                .eq('workspace_id', activeWorkspace.id)
                .in('id', idsToUpdate);

            if (error) throw error;

            // Force fetch to ensure sync with server state
            if (shouldFetch) {
                // Short delay to allow DB propagation
                setTimeout(() => fetchInsightsData(), 500);
            }
        } catch (e) {
            console.error("Error marking gnome notifications as read:", e);
            // Revert optimistic update? Or just let the next fetch handle it.
            // For now, assume success.
        }
    }, [activeWorkspace]);

const markInsightsAsRead = React.useCallback(async (gnomeId) => {
    if (!activeWorkspace) return;

    const targetGnomeId = String(gnomeId);

    // Find insights to update
    const insightsToUpdate = insights.filter(i => String(i.gnome_id) === targetGnomeId);
    const insightIds = insightsToUpdate.map(i => i.id);

    if (insightIds.length === 0) return;

    // Optimistic update
    setInsights(prev => prev.filter(i => !insightIds.includes(i.id)));

    try {
        const { error } = await supabase
            .from('gnome_insights')
            .update({ is_read: true })
            .eq('workspace_id', activeWorkspace.id)
            .in('id', insightIds);

        if (error) throw error;
    } catch (e) {
        console.error("Error marking insights as read:", e);
    }
}, [activeWorkspace, insights]);

    // 4. Chat Management
    const openChat = React.useCallback(async (gnome, insight = null) => {
        if (!gnome || !activeWorkspace) return;

        setSelectedGnome(gnome);
        setSelectedInsight(insight);
        setChatModalOpen(true);

        // IMMEDIATE optimistic clearing for better UX
        // We do this immediately so the badge disappears as soon as chat opens
        markGnomeNotificationsAsRead(gnome.id, false);
        markInsightsAsRead(gnome.id);
    }, [activeWorkspace, insights, markGnomeNotificationsAsRead, markInsightsAsRead]);

    const closeChat = React.useCallback(() => {
        setChatModalOpen(false);
        setSelectedGnome(null);
        setSelectedInsight(null);
    }, []);

    const setChatDraft = React.useCallback((gnomeId, draft) => {
        setChatDrafts(prev => ({
            ...prev,
            [gnomeId]: draft
        }));
    }, []);

    // Helpers
    const getCooldownStatus = () => {
        if (!latestInsightDate) {
            return { canGenerate: true, hours: 0, minutes: 0 };
        }

        // Use a 1-hour cooldown
        const COOLDOWN_MS = 60 * 60 * 1000;
        const diff = now.getTime() - latestInsightDate.getTime();
        const msRemaining = Math.max(0, COOLDOWN_MS - diff);

        // If diff is negative (future date?), treat as cooldown active
        // If diff >= COOLDOWN_MS, we are good.
        const canGenerate = diff >= COOLDOWN_MS;

        const hours = Math.floor(msRemaining / (1000 * 60 * 60));
        const minutes = Math.floor((msRemaining % (1000 * 60 * 60)) / (1000 * 60));

        return { canGenerate, hours, minutes };
    };

    const { canGenerate, hours, minutes } = getCooldownStatus();

    const value = React.useMemo(() => ({
            gnomes,
            insights,
            gnomeNotifications,
            markGnomeNotificationsAsRead,
        markInsightsAsRead,
            latestInsightDate,
            generating,
            generateInsights,
            openChat,
            closeChat,
            chatModalOpen,
            selectedGnome,
            selectedInsight,
            canGenerate,
            hoursUntilNext: hours,
            minutesUntilNext: minutes,
            chatDrafts,
            setChatDraft
        }), [
            gnomes,
            insights,
            gnomeNotifications,
            markGnomeNotificationsAsRead,
        markInsightsAsRead,
            openChat,
            closeChat,
            latestInsightDate,
            generating,
            chatModalOpen,
            selectedGnome,
            selectedInsight,
            canGenerate,
            hours,
            minutes,
            chatDrafts,
            setChatDraft
        ]);

    return (
        <GnomeContext.Provider value={value}>
            {children}
        </GnomeContext.Provider>
    );
};
